import { parse } from 'node-html-parser';
import juice from 'juice';

import type { ContentType, ContentTypeObject, CustomHtmlElement } from '../types/pbTypes';

import { getContentTypeConfig } from './config';
import { getContentType } from './utils';

/**
 * Create a content type object
 * @param type
 * @param node
 */
export const createContentTypeObject = (type: ContentType, node?: CustomHtmlElement): ContentTypeObject => {
    return {
        contentType: type,
        appearance: node?.getAttribute('data-appearance') ?? undefined,
        children: [],
        uniqueID: '',
    };
};

/**
 * Walk the node and create a content type object
 * @param rootEl
 * @param contentTypeStructureObj
 */
const walkNode = (rootEl: CustomHtmlElement, contentTypeStructureObj: ContentTypeObject) => {
    /**
     * Walk the parent node
     * @param currentNode
     */
    const walkParentNode = (currentNode: CustomHtmlElement) => {
        if (currentNode.childNodes) {
            for (const childNode of currentNode.childNodes) {
                walkChildNode(childNode as CustomHtmlElement);
            }
        }
    };

    /**
     * Walk the child node
     * @param currentNode
     */
    const walkChildNode = (currentNode: CustomHtmlElement) => {
        const contentType =
            currentNode.nodeType === 1 ? getContentType(currentNode.attributes['data-content-type']) : false;

        if (!contentType) {
            walkParentNode(currentNode);
        } else {
            const props = createContentTypeObject(contentType, currentNode);
            const contentTypeConfig = getContentTypeConfig(contentType);

            if (contentTypeConfig && typeof contentTypeConfig.configAggregator === 'function') {
                try {
                    Object.assign(props, contentTypeConfig.configAggregator(currentNode, props));
                } catch (e) {
                    console.error(`Failed to aggregate config for content type ${contentType}.`, e);
                }
            } else {
                console.warn(
                    `Page Builder ${contentType} content type is not supported, this content will not be rendered.`,
                );
            }

            if (!currentNode.attributes['data-pb-style']) {
                const childNodes = currentNode.childNodes;

                if (childNodes) {
                    for (const childNode of childNodes) {
                        if (
                            childNode.attributes &&
                            !childNode.attributes['data-pb-style'] &&
                            childNode.attributes['data-element']
                        ) {
                            const firstChild = childNode.firstChild as CustomHtmlElement;
                            props.uniqueID = firstChild.attributes['data-pb-style'];
                        }
                    }
                }
            }

            contentTypeStructureObj?.children?.push(props);

            walkNode(currentNode, props);
        }
    };

    walkParentNode(rootEl);

    return contentTypeStructureObj;
};

/**
 * Parse the HTML string and convert it to a ContentTypeObject
 *
 * @param {String} htmlStr
 * @returns {Object}
 */
export const parseStorageHtml = (htmlStr: string): ContentTypeObject | undefined => {
    let result;

    try {
        result = juice("<div id='html-body'>" + htmlStr + '</div>');
    } catch (event) {
        console.error(`Failed to inline styles in HTML string.`, event);

        return;
    }

    let root;

    try {
        root = parse(result);
    } catch (event) {
        console.error(`Failed to parse HTML string.`, event);

        return;
    }

    const stageContentType = createContentTypeObject('root-container');

    return walkNode(root, stageContentType);
};

export default parseStorageHtml;
